import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  MagnifyingGlass as SearchIcon,
  PencilSimple as EditIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable';
import { mailCampaignsApi } from '../../lib/directMailApi';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  MAIL_TEMPLATE_LABELS,
  type MailCampaign,
  type MailCampaignStatus,
} from '../../types/directMail';
import { DirectMailPageHeader, DIRECT_MAIL_ROUTES } from '../../components/directmail/DirectMailNav';
import { CampaignFormModal } from '../../components/directmail/CampaignFormModal';
import { loadAllPages } from '../../components/directmail/loadAllPages';
import { ROOF_QUOTE_SETUP_PATH, useRoofQuoteWidget } from '../../components/directmail/roofQuotePricing';

const STATUS_COLORS: Record<MailCampaignStatus, 'primary' | 'neutral' | 'success'> = {
  draft: 'neutral',
  active: 'success',
  archived: 'neutral',
};

interface SnackbarState {
  open: boolean;
  message: string;
  color: 'success' | 'danger' | 'warning' | 'neutral';
}

export const DirectMailCampaignsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MailCampaignStatus | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MailCampaign | null>(null);
  const [deleting, setDeleting] = useState<MailCampaign | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    color: 'neutral',
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  // The list is drained page by page rather than asked for a big page: the API
  // ignores `page_size`, so a single request would silently show the first 20
  // campaigns and hide the rest behind no pagination control at all.
  const campaignsQuery = useQuery({
    queryKey: ['direct-mail', 'campaigns', search, statusFilter],
    queryFn: () =>
      loadAllPages((page) =>
        mailCampaignsApi.list({
          search: search || undefined,
          status: statusFilter ?? undefined,
          page,
        }),
      ),
  });

  const widgetQuery = useRoofQuoteWidget();
  const hasTiers = (widgetQuery.data?.tiers.length ?? 0) > 0;

  const notify = (message: string, color: SnackbarState['color']) =>
    setSnackbar({ open: true, message, color });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['direct-mail', 'campaigns'] });

  const remove = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await mailCampaignsApi.remove(deleting.id);
      notify(`${deleting.name} deleted.`, 'success');
      setDeleting(null);
      void refresh();
    } catch (err) {
      notify(getApiErrorMessage(err, 'That campaign could not be deleted.'), 'danger');
    } finally {
      setDeleteBusy(false);
    }
  };

  const campaigns = campaignsQuery.data ?? [];

  const columns: ResponsiveColumn<MailCampaign>[] = [
    {
      key: 'name',
      sortValue: (row) => row.name,
      header: 'Campaign',
      width: '28%',
      mobileVariant: 'header',
      render: (campaign) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography level="title-sm">{campaign.name}</Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {campaign.headline || 'No headline set'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'template',
      sortValue: (row) => row.template_kind,
      header: 'Piece',
      width: '15%',
      render: (campaign) => (
        <Chip size="sm" variant="soft">
          {MAIL_TEMPLATE_LABELS[campaign.template_kind]}
        </Chip>
      ),
    },
    {
      key: 'tier',
      sortValue: (row) => row.quote_tier_name,
      header: 'Pricing tier',
      width: '18%',
      render: (campaign) => (
        <Typography level="body-sm">
          {campaign.quote_tier_name || 'Default (Better)'}
        </Typography>
      ),
    },
    {
      key: 'status',
      sortValue: (row) => row.status,
      header: 'Status',
      width: '12%',
      render: (campaign) => (
        <Chip size="sm" variant="soft" color={STATUS_COLORS[campaign.status]}>
          {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
        </Chip>
      ),
    },
    {
      key: 'pins',
      sortValue: (row) => row.pin_count,
      header: 'Pins',
      width: '10%',
      render: (campaign) => <Typography level="body-sm">{campaign.pin_count ?? 0}</Typography>,
    },
    {
      key: 'created',
      sortValue: (row) => row.created_at ? new Date(row.created_at) : null,
      header: 'Created',
      width: '12%',
      render: (campaign) => (
        <Typography level="body-sm">
          {campaign.created_at ? format(parseISO(campaign.created_at), 'MMM d, yyyy') : '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <DirectMailPageHeader
        title="Campaigns"
        description="One campaign is one offer: a piece type, a pricing tier, and the copy printed on every house in it."
        actions={
          <Button startDecorator={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true); }}>
            New campaign
          </Button>
        }
      />

      {widgetQuery.data && !hasTiers && (
        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
          <Box>
            <Typography level="title-sm">Set your prices before you mail anything</Typography>
            <Typography level="body-sm">
              Direct mail prints the price your Roof Quote Widget would quote. With no tier
              configured there is no price to print, so pins in any campaign stay unpriced.
            </Typography>
            <Button
              component={RouterLink}
              to={ROOF_QUOTE_SETUP_PATH}
              size="sm"
              variant="plain"
              sx={{ px: 0, mt: 0.5 }}
            >
              Set up Roof Quote pricing
            </Button>
          </Box>
        </Alert>
      )}

      <Card variant="outlined" sx={{ p: 2, gap: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Search</FormLabel>
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              startDecorator={<SearchIcon />}
              placeholder="Name or headline"
            />
          </FormControl>
          <FormControl sx={{ minWidth: { sm: 200 } }}>
            <FormLabel>Status</FormLabel>
            <Select
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value)}
              slotProps={{ button: { 'aria-label': 'Filter by status' } }}
            >
              <Option value={null}>All statuses</Option>
              <Option value="active">Active</Option>
              <Option value="draft">Draft</Option>
              <Option value="archived">Archived</Option>
            </Select>
          </FormControl>
        </Stack>

        {campaignsQuery.isLoading ? (
          <Stack spacing={1}>
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} variant="rectangular" height={52} sx={{ borderRadius: 'sm' }} />
            ))}
          </Stack>
        ) : campaignsQuery.isError ? (
          <Alert color="danger" variant="soft">
            <Box>
              <Typography level="title-sm">Campaigns could not be loaded</Typography>
              <Typography level="body-sm">
                {getApiErrorMessage(campaignsQuery.error, 'Please try again in a moment.')}
              </Typography>
              <Button
                size="sm"
                variant="plain"
                sx={{ px: 0, mt: 0.5 }}
                onClick={() => void campaignsQuery.refetch()}
              >
                Retry
              </Button>
            </Box>
          </Alert>
        ) : (
          <ResponsiveTable<MailCampaign>
            rows={campaigns}
            getRowKey={(campaign) => campaign.id}
            columns={columns}
            actionsWidth="10%"
            emptyState={
              <Sheet variant="soft" sx={{ p: 4, borderRadius: 'sm', textAlign: 'center' }}>
                <Typography level="title-md">
                  {search || statusFilter ? 'No campaigns match that filter' : 'No campaigns yet'}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 1, mb: 2 }}>
                  {search || statusFilter
                    ? 'Clear the filters to see every campaign.'
                    : 'A campaign holds the piece type, the pricing tier and the copy. Create one before you pin houses so the pins have somewhere to go.'}
                </Typography>
                <Button
                  startDecorator={<AddIcon />}
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  New campaign
                </Button>
              </Sheet>
            }
            rowActions={(campaign) => (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Edit campaign">
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => {
                      setEditing(campaign);
                      setFormOpen(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete campaign">
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => setDeleting(campaign)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          />
        )}
      </Card>

      <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 2 }}>
        Next: pin houses on the{' '}
        <Button
          component={RouterLink}
          to={DIRECT_MAIL_ROUTES.map}
          variant="plain"
          size="sm"
          sx={{ px: 0.5, minHeight: 0 }}
        >
          map
        </Button>
        , then measure them in the{' '}
        <Button
          component={RouterLink}
          to={DIRECT_MAIL_ROUTES.queue}
          variant="plain"
          size="sm"
          sx={{ px: 0.5, minHeight: 0 }}
        >
          queue
        </Button>
        .
      </Typography>

      <CampaignFormModal
        open={formOpen}
        campaign={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(campaign, created) => {
          setFormOpen(false);
          notify(created ? `${campaign.name} created.` : `${campaign.name} saved.`, 'success');
          void refresh();
        }}
      />

      <Modal open={deleting !== null} onClose={() => setDeleting(null)}>
        <ModalDialog sx={{ width: { xs: '92vw', sm: 440 } }}>
          <DialogTitle>Delete this campaign?</DialogTitle>
          <DialogContent>
            <Typography level="body-sm">
              {deleting?.name} has {deleting?.pin_count ?? 0} pins. Deleting it leaves those pins
              without a campaign — they stay in the queue but cannot be batched until you assign
              them somewhere else. A campaign that has already mailed a batch can only be archived.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="danger" loading={deleteBusy} onClick={() => void remove()}>
              Delete campaign
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={snackbar.open}
        color={snackbar.color}
        variant="soft"
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};
